import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  StatCard,
} from '@assist/ui';
import {
  ApiError,
  endpoints,
  type DashboardResponse,
  type FindingSummary,
  type HomeResponse,
  type LocationRow,
  type MetricsResponse,
  type TenantIdentity,
} from '../state/api.js';
import { useSession } from '../state/session.js';
import { formatMoney, formatNumber } from '../lib/format.js';
import { usePeriode } from '../state/periode.js';

/**
 * Layar Dashboard — Figma `5:2` · M11 (menampilkan M02, M03) · rute `/dashboard`.
 *
 * Izin: LDR MGR, ditambah AUD untuk MEMBACA (koreksi peta P-2 di RANCANGAN §8.1).
 * Auditor memperoleh halaman pendaratan tetapi TIDAK memperoleh antrean
 * keputusan — Approve/Decline adalah pekerjaan persetujuan, bukan pekerjaan audit.
 *
 * Kelima kondisi peta §5.1 ada di sini: baku · memuat (KERANGKA, bukan pemintal)
 * · kosong (belum ada temuan bulan ini) · gagal memuat · sebagian modul di luar
 * paket (kartu DISEMBUNYIKAN, bukan dimatikan).
 */
export function Dashboard({ onNavigate }: { onNavigate: (route: string) => void }) {
  const { t, locale, hasModule, me } = useSession();
  /**
   * PERIODE dari bilah atas (`5:58`). Layar ini adalah salah satu dari tiga yang
   * rutenya benar-benar menerimanya; mengganti bulan memuat ulang angka dasbornya.
   */
  const { periode } = usePeriode();
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  /**
   * Antrean keputusan — barisnya, dari `GET /v1/dashboard/decision-queue`.
   *
   * Ia `catch`-nya sendiri dan TIDAK menggagalkan seluruh layar: seorang leader yang
   * kehilangan antrean keputusan tetap harus melihat penghitung dan kondisi areanya.
   * Sebuah `Promise.all` yang menjatuhkan tiga permintaan karena satu gagal mengubah
   * kegagalan sebagian menjadi layar galat penuh.
   */
  const [decisions, setDecisions] = useState<Array<FindingSummary & { queue_reason: string }>>([]);
  /**
   * DUA sumber tambahan yang dituntut audit L-02, dan keduanya SUDAH ADA sebagai rute —
   * yang belum ada hanyalah pemanggilnya:
   *
   *   · `GET /v1/dashboard` → `areas[]` dengan `completion_pct`, `overdue_count`, dan
   *     `last_audit_pct`. Inilah yang mengisi tabel *Area condition* (Figma `6:90`);
   *     sebelumnya layar ini merender tumpukan kartu dari `GET /v1/locations`, yang tidak
   *     punya satu pun dari ketiga kolom itu.
   *   · `GET /v1/metrics` → `by_category[]`. Inilah yang mengisi kartu *Recurring
   *     findings* (Figma `6:55`).
   *
   * Keduanya ber-`catch` sendiri: sebuah dasbor yang gagal seluruhnya karena satu kartu
   * tidak dapat dimuat menukar kegagalan sebagian dengan kegagalan penuh.
   */
  const [dash, setDash] = useState<DashboardResponse | null>(null);
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  /** Baris yang sedang menunggu jawaban transisi, supaya tombolnya tidak ditekan dua kali. */
  const [acting, setActing] = useState<string | null>(null);
  const [userCount, setUserCount] = useState(0);
  /**
   * Kontrol tersegmentasi `Needs attention · All areas` — Figma `6:90` kanan atas.
   *
   * Ia bukan hiasan: tabel yang menampilkan 40 area menuntut manajer memindai untuk
   * menemukan empat yang bermasalah, dan "yang bermasalah" adalah satu-satunya alasan
   * ia membuka tabel ini. Saringannya memakai definisi yang SAMA dengan kolom Status,
   * jadi tidak ada baris yang berstatus Kritis dan hilang dari "perlu perhatian".
   */
  const [hanyaPerluPerhatian, setHanyaPerluPerhatian] = useState(true);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<ApiError | null>(null);
  /**
   * Identitas organisasi, dimuat OLEH LAYAR INI dan bukan dititipkan ke `GET /v1/me`.
   *
   * `logo_mark_url` berumur 90 detik. `GET /v1/me` dipanggil sekali pada awal sesi, jadi
   * URL yang dititipkan di sana sudah mati ketika orang itu membuka Dasbor beberapa menit
   * kemudian — dan yang terlihat adalah gambar rusak, bukan logo yang hilang. Dimuat di
   * sini, ia selalu baru.
   */
  const [identity, setIdentity] = useState<TenantIdentity | null>(null);

  async function load() {
    setState('loading');
    setError(null);
    try {
      const [homePayload, locationPayload] = await Promise.all([
        endpoints.home(),
        endpoints.locations(),
      ]);
      setHome(homePayload);
      setLocations(locationPayload.items);
      setState('ready');
      try {
        setUserCount((await endpoints.users()).items.length);
      } catch {
        setUserCount(0);
      }
      try {
        setDash(await endpoints.dashboard(periode));
      } catch {
        setDash(null);
      }
      try {
        setMetrics(await endpoints.metrics());
      } catch {
        setMetrics(null);
      }
      try {
        setDecisions((await endpoints.decisionQueue()).items);
      } catch {
        setDecisions([]);
      }
      /*
       * Kegagalan memuat identitas TIDAK menjatuhkan Dasbor, dan itu keputusan: logo
       * adalah hiasan yang berguna, dan sebuah layar yang menolak memuat karena logonya
       * tidak terbaca menukar kerugian kecil dengan kerugian besar.
       */
      try {
        setIdentity(await endpoints.tenantIdentity());
      } catch {
        setIdentity(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
      setState('error');
    }
  }

  useEffect(() => {
    void load();
    // `periode` ada di sini dengan sengaja: mengganti bulan di bilah atas HARUS memuat
    // ulang, karena kalau tidak, pemilihnya berubah dan angkanya tidak — yaitu bentuk
    // paling halus dari kendali yang berbohong.
  }, [periode]);

  if (state === 'loading') {
    return (
      <section className="page">
        <h1 className="page__title">{t('dashboard.title')}</h1>
        <LoadingState rows={4} label={t('state.loading.title')} />
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className="page">
        <h1 className="page__title">{t('dashboard.title')}</h1>
        <ErrorState
          title={t('state.error.title')}
          body={t('state.error.body')}
          code={error?.problem.request_id ?? null}
          codeLabel={
            error?.problem.request_id
              ? t('state.error.code', { code: error.problem.request_id })
              : undefined
          }
          retryLabel={t('app.retry')}
          onRetry={() => void load()}
        />
      </section>
    );
  }

  // Kartu yang disumbang M02 lewat `enhances` → `kernelX.MetricSourcePort`.
  // Modul yang tidak ter-entitlement TIDAK mengirim kartunya sama sekali; layar
  // ini karena itu tidak pernah menampilkan angka nol yang menyesatkan.
  const findingsCard = home?.cards.find((c) => c.card === 'MY_TASKS');
  const counters = findingsCard?.counters;
  const hasFindings = hasModule('M02') && counters !== undefined;
  const noFindingsThisMonth =
    hasFindings && counters.open === 0 && counters.closedThisMonth === 0;

  /**
   * TIGA KEPUTUSAN PER BARIS — Figma `6:11`, `6:26`, `6:41`.
   *
   * Ketiganya transisi alur kerja yang SUDAH ADA di `workflows/finding.v1.json` dan
   * SUDAH punya rutenya: `CONFIRM_FIX` (Approve), `SEND_BACK` (Decline), `REQUEST_INFO`
   * (Request info). Tidak ada satu pun aksi baru yang dikarang di sini — yang hilang
   * sebelumnya hanyalah tombolnya, dan tanpanya antrean keputusan adalah daftar bacaan
   * yang menuntut lima ketukan per butir untuk memutuskan satu hal.
   *
   * Ketiganya hanya sah dari keadaan `AWAITING_VERIFICATION` (`SEND_BACK` dan
   * `CONFIRM_FIX`), jadi baris yang berada di antrean karena TERLEWAT TENGGAT hanya
   * memperoleh `REQUEST_INFO`. Menampilkan tombol yang pasti dijawab 422 adalah
   * kebohongan kecil yang mahal: operator menekannya, gagal, dan berhenti memercayai
   * bilah aksinya.
   */
  async function decide(item: FindingSummary & { queue_reason: string }, transition: string) {
    if (!item.code) return;
    setActing(item.id);
    try {
      await endpoints.transition(item.code, { transition });
      setDecisions((rows) => rows.filter((r) => r.id !== item.id));
    } catch {
      // Kegagalan transisi TIDAK menghapus barisnya: ia tetap di antrean, dan layar
      // memuat ulang supaya keadaan yang ditampilkan adalah keadaan yang sesungguhnya.
      void load();
    } finally {
      setActing(null);
    }
  }

  /**
   * KATEGORI YANG PALING SERING BERULANG — kartu `Recurring findings` (Figma `6:55`).
   *
   * BATAS YANG PERLU DINYATAKAN, dan ia keputusan sadar. Figma memberi baris berupa
   * TEMA — "Items without labels", "Waste not removed" — dan produk ini tidak memodelkan
   * tema; ia memodelkan KATEGORI 5R. Mengarang tema dari judul temuan akan menjadi
   * penggolongan buatan yang tidak dapat dipertanggungjawabkan kepada manajer yang
   * memakainya. Jadi barisnya adalah kategori 5R yang paling sering dibuka pada periode
   * berjalan, dari `GET /v1/metrics` → `by_category` — sinyal SUNGGUHAN yang paling dekat.
   *
   * Kalimat kesimpulan Figma ("All three point to a standard that does not exist yet.")
   * adalah penilaian manusia; sebuah produk yang menuliskannya sendiri akan mengarang
   * sebab. Yang menggantikannya adalah kalimat faktual berisi periodenya, dan tombol
   * teksnya menuju Laporan — bukan "Draft the standard", karena modul Standar (M08)
   * belum dibangun dan tombol yang tidak menuju ke mana pun lebih buruk daripada tombol
   * yang tidak ada.
   */
  const recurring = (metrics?.by_category ?? [])
    .filter((c) => c.category_code !== null && c.opened > 0)
    .sort((a, b) => b.opened - a.opened)
    .slice(0, 4);
  const recurringMax = Math.max(1, ...recurring.map((c) => c.opened));

  const areas = dash?.areas ?? [];
  /** Satu-satunya angka di layar yang menjawab "apa untungnya" kepada pimpinan pabrik. */
  const impact = dash?.verified_impact ?? null;

  function areaStatus(a: DashboardResponse['areas'][number]): {
    tone: 'success' | 'warning' | 'danger';
    key: string;
  } {
    if (a.overdue_count > 0) return { tone: 'danger', key: 'CRITICAL' };
    if ((a.completion_pct ?? 100) < 80) return { tone: 'warning', key: 'WATCH' };
    return { tone: 'success', key: 'GOOD' };
  }

  const areaTerlihat = hanyaPerluPerhatian
    ? areas.filter((a) => areaStatus(a).key !== 'GOOD')
    : areas;

  return (
    <section className="page">
      <header className="page__head">
        <div>
          {/*
            JUDUL DUA WARNA dari DUA kunci — audit L-02 dan §5. Urutan katanya MEMBALIK
            antarbahasa (`Operations`+`Dashboard` versus `Dasbor`+`Operasional`), jadi
            menyambung dua string akan memaksa satu urutan pada kedua bahasa.
          */}
          <h1 className="page__title" data-figma="dashboard.judul">
            {t('dashboard.title')}{' '}
            <span className="page__title-accent">{t('dashboard.titleAccent')}</span>
          </h1>
          {/*
            LOGO ORGANISASI — Figma `216:2962` (EN) / `216:3245` (ID), node `220:2234`.
            TEMPAT LOGO ITU BENAR-BENAR TAMPIL BAGI PELANGGAN.

            Ia berdiri DI SAMPING nama organisasi pada subjudul `5:69`, dan bukan di
            bilah atas. Alasannya tertulis di penyimpangan V-01 (`Shell.tsx`): nama
            organisasi dihapus dari bilah atas bagi pengguna tenant justru supaya bilah
            atasnya sama persis dengan yang digambar 28 frame, dan V-01 sendiri berbunyi
            "Figma menaruhnya di subjudul Dasbor (5:69), dan untuk pengguna tenant itu
            memang cukup". Menaruh logo di bilah atas akan membuka kembali kelas yang
            baru saja ditutup; menaruhnya di sini mengikuti kalimat itu apa adanya.

            ISOLASI: `GET /v1/tenant/identity` tidak menerima satu pun pengenal —
            organisasinya datang dari sesi yang sudah terikat, jadi tidak ada bentuk
            permintaan yang dapat meminta logo organisasi lain.

            Ia dirender HANYA pada `logo_state === 'READY'`. Belum ada logo dan logo yang
            byte-nya belum selesai diperiksa sama-sama tidak merender apa pun: sebuah
            kotak kosong di samping nama organisasi terbaca sebagai "logonya rusak",
            yang berbeda dari "belum ada logo" dan belum tentu benar.
          */}
          {identity?.logo_state === 'READY' && identity.logo_mark_url ? (
            <img
              className="page__org-logo"
              data-figma="dashboard.logoOrganisasi"
              src={identity.logo_mark_url}
              alt=""
              width={24}
              height={24}
            />
          ) : null}
          {/* Subjudul Figma: organisasi · jumlah area · jumlah pengguna aktif. */}
          <p className="page__subtitle" data-figma="dashboard.subjudul">
            {t('dashboard.subtitle', {
              org: me?.tenant?.name ?? '—',
              areas: locations.length,
              /**
                 Jumlah pengguna aktif TIDAK tersedia bagi sesi tenant: satu-satunya
                 rute yang menghitungnya adalah `GET /v1/platform/tenants` (izin PLT,
                 lewat `app.tenant_user_counts()`), dan memberi manajer angka itu
                 menuntut rute baru. Sampai rute itu ada, yang ditampilkan adalah
                 jumlah PENGGUNA YANG TERLIHAT baginya — angka yang benar untuk
                 pertanyaan "berapa orang di lingkup saya", dan yang tidak berpura-pura
                 menjadi angka organisasi.
               */
              users: userCount,
            })}
          </p>
        </div>
        <div className="page__actions">
          {/* Figma: `Export` sekunder + `Build monthly report` PRIMER navy. Kode
              sebelumnya hanya punya yang kedua, dan merendernya sebagai sekunder. */}
          <Button variant="secondary" onClick={() => onNavigate('/reports')}>
            {t('dashboard.export')}
          </Button>
          <Button variant="primary" onClick={() => onNavigate('/reports')}>
            {t('dashboard.buildMonthlyReport')}
          </Button>
        </div>
      </header>

      {noFindingsThisMonth ? (
        <EmptyState title={t('dashboard.empty.title')} body={t('dashboard.empty.body')} />
      ) : null}

      {hasFindings && !noFindingsThisMonth ? (
        <div className="stat-grid" data-figma="dashboard.kartu">
          {/*
            LABEL STATISTIK TIDAK LAGI BERWARNA. Figma `14:55`: seluruh label `ink/50`
            polos, dan arti dibawa PIL TREN. Kode sebelumnya mewarnai labelnya merah,
            amber, atau hijau dan menambah ikon status — sehingga sebuah dasbor yang
            sehat tetap menampilkan tiga label berwarna, dan warna berhenti berarti.
            `tone` kini hanya dipakai ketika ia benar-benar keadaan: tenggat terlewat.
          */}
          <StatCard
            label={t('dashboard.stat.openFindings')}
            value={formatNumber(counters.open, locale)}
            onClick={() => onNavigate('/findings')}
          />
          <StatCard
            label={t('dashboard.stat.overdue')}
            value={formatNumber(counters.overdue, locale)}
            trend={counters.overdue > 0 ? t('dashboard.trend.needsAction') : undefined}
            trendDirection={counters.overdue > 0 ? 'down' : 'flat'}
            onClick={() => onNavigate('/findings?overdue=true')}
          />
          <StatCard
            label={t('dashboard.stat.awaitingVerification')}
            value={formatNumber(counters.awaiting, locale)}
            onClick={() => onNavigate('/findings?state=AWAITING_VERIFICATION')}
          />
          {impact ? (
            <StatCard
              label={t('dashboard.stat.verifiedImpact')}
              value={formatMoney(impact.amount, impact.currency, locale)}
              caption={
                impact.excluded_unverified_count > 0
                  ? t('dashboard.impact.excluded', { count: impact.excluded_unverified_count })
                  : undefined
              }
            />
          ) : (
            <StatCard
              label={t('dashboard.stat.closedThisMonth')}
              value={formatNumber(counters.closedThisMonth, locale)}
            />
          )}
        </div>
      ) : null}

      {/*
        BARIS TENGAH DUA KOLOM — 840 / 272, jarak 16. Figma `6:3` w=840 dan `6:55` w=272
        pada x=856. Kode sebelumnya merender keduanya sebagai kartu berturut-turut dalam
        satu kolom penuh, dan halamannya menjadi 1613 px tinggi untuk rancangan yang muat
        dalam 900. Angkanya TETAP seperti Figma — bukan `1fr 1fr` — karena kolom kanan
        adalah kartu ringkas yang tidak boleh melebar bersama layar.
      */}
      <div className="dashboard__middle">
        {me?.role_code !== 'AUD' ? (
          <section className="card" data-figma="dashboard.antrean">
            <header className="card__head">
              <h2 className="card__title">{t('dashboard.decisionQueue')}</h2>
              <Badge tone="warning">{formatNumber(decisions.length, locale)}</Badge>
              <Button variant="text" onClick={() => onNavigate('/verification-queue')}>
                {t('dashboard.decisionQueue.open')}
              </Button>
            </header>
            {decisions.length === 0 ? (
              <p className="card__muted">{t('dashboard.decisionQueue.empty')}</p>
            ) : (
              <ul className="decision-list">
                {decisions.slice(0, 3).map((item) => (
                  <li key={item.id} className="decision-row">
                    <div className="decision-row__body">
                      <button
                        type="button"
                        className="linkish decision-row__title"
                        onClick={() =>
                          item.code && onNavigate(`/findings/${encodeURIComponent(item.code)}`)
                        }
                      >
                        {item.title}
                      </button>
                      <p className="decision-row__meta">
                        {[
                          item.category_code ? t(`category.${item.category_code}`) : null,
                          item.owner?.full_name ?? null,
                          item.location?.name ?? item.location?.code ?? null,
                          t(`dashboard.queueReason.${item.queue_reason}`),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="decision-row__actions">
                      {item.state === 'AWAITING_VERIFICATION' ? (
                        <>
                          <Button
                            variant="action"
                            busy={acting === item.id}
                            onClick={() => void decide(item, 'CONFIRM_FIX')}
                          >
                            {t('dashboard.decision.approve')}
                          </Button>
                          <Button
                            variant="secondary"
                            busy={acting === item.id}
                            onClick={() => void decide(item, 'SEND_BACK')}
                          >
                            {t('dashboard.decision.decline')}
                          </Button>
                        </>
                      ) : null}
                      <Button
                        variant="secondary"
                        busy={acting === item.id}
                        onClick={() => void decide(item, 'REQUEST_INFO')}
                      >
                        {t('dashboard.decision.requestInfo')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <section className="card" data-figma="dashboard.antrean">
            <h2 className="card__title">{t('dashboard.decisionQueue')}</h2>
            <p className="card__muted">{t('dashboard.decisionQueue.auditor')}</p>
          </section>
        )}

        <section className="card" data-figma="dashboard.berulang">
          <header className="card__head">
            <h2 className="card__title">{t('dashboard.recurring')}</h2>
            <span className="card__meta">{t('dashboard.recurring.period')}</span>
          </header>
          {recurring.length === 0 ? (
            <p className="card__muted">{t('dashboard.recurring.empty')}</p>
          ) : (
            <>
              <ul className="recurring-list">
                {recurring.map((c) => (
                  <li key={c.category_code}>
                    <span className="recurring-list__label">
                      {t(`category.${c.category_code}`)}
                    </span>
                    <span className="recurring-list__value">{formatNumber(c.opened, locale)}</span>
                    <span className="recurring-list__track">
                      <span
                        className="recurring-list__fill"
                        style={{ width: `${Math.round((c.opened / recurringMax) * 100)}%` }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
              <p className="card__muted">{t('dashboard.recurring.note')}</p>
              <Button variant="text" onClick={() => onNavigate('/reports')}>
                {t('dashboard.recurring.action')}
              </Button>
            </>
          )}
        </section>
      </div>

      {/*
        TABEL KONDISI AREA — Figma `6:90`. Kode sebelumnya merender satu `ProgressBar`
        selebar halaman lalu LIMA KARTU BERBINGKAI bertumpuk, yang berarti lima area
        memakan 500 px vertikal untuk lima angka. Tabel Figma memakai 39 px kepala dan
        47 px per baris, dan ia memuat tiga kolom yang tumpukan kartu itu tidak punya
        sama sekali: Completion, Overdue, dan Last audit.
      */}
      <section className="card" data-figma="dashboard.area">
        <header className="card__head">
          <h2 className="card__title">{t('dashboard.areaCondition')}</h2>
          <div className="segmented" role="group" aria-label={t('dashboard.areaCondition')}>
            <button
              type="button"
              className={hanyaPerluPerhatian ? 'is-active' : ''}
              aria-pressed={hanyaPerluPerhatian}
              onClick={() => setHanyaPerluPerhatian(true)}
            >
              {t('dashboard.areaFilter.needsAttention')}
            </button>
            <button
              type="button"
              className={!hanyaPerluPerhatian ? 'is-active' : ''}
              aria-pressed={!hanyaPerluPerhatian}
              onClick={() => setHanyaPerluPerhatian(false)}
            >
              {t('dashboard.areaFilter.all')}
            </button>
          </div>
        </header>
        {areaTerlihat.length === 0 ? (
          <p className="card__muted">
            {areas.length === 0
              ? t('dashboard.areaCondition.empty')
              : t('dashboard.areaCondition.allGood')}
          </p>
        ) : (
          <table className="table table--area">
            <thead>
              <tr>
                <th scope="col">{t('dashboard.areaColumn.area')}</th>
                <th scope="col">{t('dashboard.areaColumn.completion')}</th>
                <th scope="col">{t('dashboard.areaColumn.overdue')}</th>
                {/*
                  KOLOMNYA MENYEBUT APA YANG BENAR-BENAR DITAMPILKAN.

                  Figma `6:90` menulis `Last audit` dan mengisinya dengan TANGGAL
                  (`11 Sep`). Rute `GET /v1/dashboard` mengirim `last_audit_pct` — SKOR,
                  bukan tanggal — dan tanggalnya tidak ada di kontrak mana pun. Menaruh
                  skor di bawah judul "Audit terakhir" membuat "76%" terbaca sebagai
                  tanggal yang rusak. Jadi judulnya yang menyesuaikan sampai kontraknya
                  membawa tanggalnya; lihat sisa pekerjaan di PERBAIKAN-KEPATUHAN-FIGMA.md.
                */}
                <th scope="col">{t('dashboard.areaColumn.lastAuditScore')}</th>
                <th scope="col">{t('dashboard.areaColumn.status')}</th>
              </tr>
            </thead>
            <tbody>
              {areaTerlihat.map((a) => {
                const status = areaStatus(a);
                return (
                  <tr key={a.location.id}>
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => onNavigate('/locations')}
                      >
                        {a.location.code} · {a.location.name}
                      </button>
                    </td>
                    {/* Bilah ketuntasan MEWARISI nada statusnya — Figma `6:90` mewarnai
                        batangnya merah/jingga/hijau, bukan biru seragam. Warna yang sama
                        dengan lencana di ujung baris berarti mata hanya perlu membaca
                        satu isyarat. */}
                    <td className="cell--completion">
                      <span
                        className={`assist-progress__track assist-progress__track--${status.tone}`}
                      >
                        <span
                          className="assist-progress__fill"
                          style={{ width: `${Math.round(a.completion_pct ?? 0)}%` }}
                        />
                      </span>
                      <span className="cell--completion-value">
                        {a.completion_pct === null ? '—' : `${Math.round(a.completion_pct)}%`}
                      </span>
                    </td>
                    {/* Jumlah tenggat terlewat MEMERAH bila bukan nol — Figma `6:90`.
                        Nol tetap netral: merah yang selalu ada berhenti berarti. */}
                    <td className={a.overdue_count > 0 ? 'cell--overdue' : ''}>
                      {formatNumber(a.overdue_count, locale)}
                    </td>
                    <td>
                      {a.last_audit_pct === null ? '—' : `${Math.round(a.last_audit_pct)}%`}
                    </td>
                    <td>
                      <Badge tone={status.tone}>{t(`dashboard.areaStatus.${status.key}`)}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}
